import { HStack, Tabs, Text } from '@chakra-ui/react';
import CockpitDialog, { CockpitDialogProps } from '@components/cockpit/CockpitDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { Breadcrumb } from '@components/cockpit/panels/Breadcrumb';
import ModifiedTag from '@components/common/form/ModifiedField';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectGroupById } from '@redux/authent/authent.selectors';
import { useCreateGroupMutation, useUpdateGroupMutation } from '@services/authent/group.api';
import { isRestrictedGroup } from '@utils/authent/profiles.utils';
import { haveSameUniqueElements } from '@utils/common.utils';
import { groupSchemaValidation } from '@validations/group.schema';
import { useFormik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { GroupWithUsers, NormalizedGroup, Profile } from '@/types/authent/groups.types';

import { useUsersManagementContext } from '../UsersManagement';
import GroupForm from './GroupForm';

type GroupDialogProps = {
  isNewGroup: boolean;
  name: string | undefined;
  profileExists: boolean;
} & Omit<CockpitDialogProps, 'children'>;

function GroupDialog({
  isNewGroup,
  name,
  profileExists,
  open,
  isLoading,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: Readonly<GroupDialogProps>) {
  return (
    <CockpitDialog
      open={open}
      isLoading={isLoading}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <Text color="neutral.100" width="100%" whiteSpace="pre-line">
        <FormattedMessage
          id={`components.usersManagement.groups.popup.${isNewGroup ? 'descriptionSaveNew' : 'descriptionSave'}`}
          values={{ name }}
        />
      </Text>
      {profileExists && (
        <Text color="neutral.100" width="100%" whiteSpace="pre-line">
          <FormattedMessage id="components.usersManagement.groups.popup.descriptionSaveWithoutProfile" />
        </Text>
      )}
    </CockpitDialog>
  );
}

const emptyGroup: Partial<NormalizedGroup> = {
  name: '',
  platforms: [],
  defaultPlatform: null,
  globalScreenConfig: null,
  inactivityTime: null,
  profile: null,
  profileName: null,
  ldapGroupDn: null,
};

export default function GroupFormContainer() {
  const { formatMessage } = useIntl();

  const { editedItem, setEditedItem } = useUsersManagementContext();
  const group = useAppSelector((state) => selectGroupById(state, editedItem?.id ?? '')) ?? emptyGroup;

  const [open, setOpen] = useState(false);
  const [createGroup, { isLoading: isLoadingCreate }] = useCreateGroupMutation();
  const [updateGroup, { isLoading: isLoadingUpdate }] = useUpdateGroupMutation();
  const isNewGroup = group.id === undefined;
  const isRestricted = isNewGroup ? false : isRestrictedGroup(group as NormalizedGroup);
  const validationSchema = useValidation(groupSchemaValidation);

  const formik = useFormik<Partial<NormalizedGroup>>({
    initialValues: group,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (isNewGroup) {
        createGroup({
          ...values,
          code: uuidv4(),
          profile: values.profile ? ({ id: Number(values.profile) } as Profile) : null,
        } as GroupWithUsers)
          .unwrap()
          .then((group) => {
            setOpen(false);
            createToast(
              formatMessage({ id: 'components.usersManagement.groups.message.add' }, { name: group.name }),
              ToastStatusEnum.SUCCESS,
            );
            setEditedItem(null);
          });
      } else {
        updateGroup({
          ...values,
          profile: values.profile ? ({ id: Number(values.profile) } as Profile) : null,
        } as GroupWithUsers)
          .unwrap()
          .then((group) => {
            setOpen(false);
            createToast(
              formatMessage({ id: 'components.usersManagement.groups.message.update' }, { name: group.name }),
              ToastStatusEnum.SUCCESS,
            );
            setEditedItem(null);
          });
      }
    },
  });

  const hasChanged = () => {
    return (
      formik.initialValues.name !== formik.values.name ||
      !haveSameUniqueElements(formik.initialValues.platforms!, formik.values.platforms!) ||
      formik.initialValues.globalScreenConfig !== formik.values.globalScreenConfig ||
      formik.initialValues.ldapGroupDn !== formik.values.ldapGroupDn ||
      formik.initialValues.inactivityTime !== formik.values.inactivityTime ||
      formik.initialValues.profile !== formik.values.profile
    );
  };

  return (
    <CockpitEditorLayout
      dialog={
        <GroupDialog
          open={open}
          isLoading={isLoadingCreate || isLoadingUpdate}
          confirmLabel={formatMessage({ id: 'global.save' })}
          cancelLabel={formatMessage({ id: 'global.cancel' })}
          onConfirm={formik.submitForm}
          onCancel={() => setOpen(false)}
          name={formik.values.name}
          profileExists={formik.values.profile === null}
          isNewGroup={isNewGroup}
        />
      }
      breadcrumbs={
        <Breadcrumb
          items={[
            {
              label: formatMessage({ id: 'components.usersManagement.groups.title' }),
              action: () => setEditedItem(null),
            },
            {
              label: isNewGroup
                ? formatMessage({
                    id: 'components.usersManagement.groups.newGroup',
                  })
                : group.name,
            },
          ]}
        />
      }
      onBack={() => setEditedItem(null)}
      onSave={() => {
        if (!formik.isValid || !hasChanged()) {
          return;
        }
        if (editedItem!.id === null) {
          void formik.submitForm();
        } else {
          setOpen(true);
        }
      }}
      isSaving={isLoadingCreate || isLoadingUpdate}
      isSaveDisabled={!formik.isValid || !hasChanged()}
    >
      <Tabs.Root orientation="vertical" defaultValue="general" lazyMount variant="vertical" userSelect="none">
        <Tabs.List>
          <Tabs.Trigger value="general">
            <HStack width="100%">
              <FormattedMessage id="components.usersManagement.groups.general.title" />
              {hasChanged() && <ModifiedTag />}
            </HStack>
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.ContentGroup backgroundColor="neutral.700">
          <Tabs.Content value="general">
            <GroupForm formik={formik} isRestricted={isRestricted} />
          </Tabs.Content>
        </Tabs.ContentGroup>
      </Tabs.Root>
    </CockpitEditorLayout>
  );
}
